import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";

import CashierSwarmActions from "src/services/swarmActions/cashier";
import { ISwarmHotKeyModel } from "src/common/swarm/models/response";
import { toastWrapper } from "src/common/components/toastWrapper";
import NumericInput from "src/common/components/numericInput";
import { ISelection } from "src/constants/interfaces/bet";
import { useOutSideClick } from "src/hooks/outSideClick";
import BottomPopover from "src/components/bottomPopover";
import { useTranslation } from "src/hooks/translation";
import { decimalString } from "src/constants/types";
import selectionsStore from "src/stores/selections";
import { getEventsCompatibilityFilter } from "src/common/helpers/eventsCompatibility";
import { getHotKeyAndSelectionEqualityChecker } from "./helpers";

import "./style.scss";

/*
{
    "SportId": 1,
    "MarketTypeId": 5498,
    "MarketType": "MatchResult",
    "SelectionTypeId": 5452,
    "SelectionType": "W1",
    "Code": "1"
}
*/

interface IProps {
  onSelectionsChange(selections: ISelection[]): void;
  isMobile?: boolean;
}

const HotKeys = ({ onSelectionsChange, isMobile }: IProps) => {
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [gameNumber, setGameNumber] = useState<decimalString>('');
  const [marketCode, setMarketCode] = useState<decimalString>('');
  const outsideRef = useOutSideClick(() => setIsOpen(false), [], '.B-betslip-numeric-keyboard ')
  const [hotKeys, setHotKeys] = useState<ISwarmHotKeyModel[]>([]);
  const disableApply = !gameNumber || !marketCode
  const { selections } = useRecoilValue(selectionsStore);

  const loadHotKeys = async () => {
    const result = await CashierSwarmActions.getHotKeys();
    setHotKeys(result);
  };

  useEffect(() => {
    loadHotKeys();
  }, [])

  const onApply = async () => {
    if (disableApply) return;

    const hotKeyModel = hotKeys.find(item => +item.Code === +marketCode)

    if (!hotKeyModel) return toastWrapper('error', 'Hot key not found!');

    const sameGameSelections = selections.filter(item => item.game.game_number === +gameNumber);

    if (sameGameSelections.some(getHotKeyAndSelectionEqualityChecker(hotKeyModel))) {
      return toastWrapper('error', 'The event is already in betslip!');
    }

    const res = await CashierSwarmActions.getHotKeyGameDetails(+gameNumber, hotKeyModel);
    const game = Object.values(res.game)[0]

    if (!game) return toastWrapper('error', 'Game or Market is not available!');

    const market = Object.values(game.market)[0]
    const event = Object.values(market.event)[0]

    const newSelection = { event, market, game };

    onSelectionsChange(selections.filter(getEventsCompatibilityFilter(newSelection)).concat(newSelection));

    setIsOpen(false)
  }

  useEffect(() => {
    if (!isOpen) {
      setGameNumber('');
      setMarketCode('');
    }
  }, [isOpen]);

  const hotKeysContent = (
    <div className="B-hot-keys-dropdown" ref={outsideRef} onClick={(e) => e.stopPropagation()}>
      <div className="B-hot-keys-dropdown-title">{t('Hot Key')}</div>
      <div className="B-hot-keys-dropdown-content">
        <NumericInput
          value={gameNumber}
          placeholder="Game Id"
          onChange={(value) => setGameNumber(value)}
          isMobile={isMobile}
        />
        <NumericInput
          value={marketCode}
          placeholder="Market Code"
          onChange={(value) => setMarketCode(value)}
          isMobile={isMobile}
        />
      </div>
      <div className="B-hot-keys-actions-container">
        {!isMobile && <div className="B-hot-keys-action-button B-cancel" onClick={() => setIsOpen(false)}>
          {t('Cancel')}
        </div>}
        <div
          className={`B-hot-keys-action-button B-apply ${disableApply ? 'B-disabled' : ''}`}
          onClick={onApply}
        >
          {t('Apply')}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="B-hot-keys-wrapper B-hot-keys-trigger" onClick={() => setIsOpen(true)}>
          {t('Hot Key')}
        </div>
        <BottomPopover
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          rootClassName="B-hot-keys-mobile-popover"
          backdropClassName="B-hot-keys-mobile-backdrop"
          sheetClassName="B-hot-keys-mobile-sheet"
        >
          {hotKeysContent}
        </BottomPopover>
      </>
    );
  }

  return (
    <div className={`B-hot-keys-wrapper B-hot-keys-desktop ${isOpen ? 'opened' : ''}`} onClick={() => setIsOpen(!isOpen)}>
      {isOpen && hotKeysContent}
      {t('Hot Key')}
    </div>
  );
};

export default HotKeys;
